import React, { useState } from 'react';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal/Modal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Table } from '@/components/ui/Table/Table';
import type { TableColumn, TableFilter } from '@/components/ui/Table/Table';
import { t } from '@/lib/i18n';
import { PersonaFormBody } from '@/routes/roros/overarching/PersonaFormBody';
import type { RorosPersonaDTO, CreatePersonaPayload } from '@/types/roros/overarching';

// ─── Add Persona popover ──────────────────────────────────────────────────────

export function AddPersonaModal({
  isOpen,
  onClose,
  availablePersonas,
  onPick,
  onCreate,
}: {
  isOpen: boolean;
  onClose: () => void;
  availablePersonas: RorosPersonaDTO[];
  onPick: (personaId: string) => Promise<void>;
  onCreate: (draft: CreatePersonaPayload) => Promise<void>;
}) {
  const [mode, setMode] = useState<'pick' | 'create'>('pick');
  const [busy, setBusy] = useState(false);

  async function submitPick(personaId: string) {
    setBusy(true);
    try {
      await onPick(personaId);
      resetAndClose();
    } finally {
      setBusy(false);
    }
  }

  function resetAndClose() {
    setMode('pick');
    onClose();
  }

  // Catalog table columns — Name / Role / Decision role / Influence / Link action.
  const columns: TableColumn<RorosPersonaDTO>[] = [
    {
      key: 'name',
      title: t('Name'),
      sortable: true,
      searchable: true,
      width: '30%',
      render: (_v, p) => <span className="text-sm font-medium text-foreground">{p.name}</span>,
    },
    {
      key: 'role',
      title: t('Role'),
      sortable: true,
      searchable: true,
      width: '25%',
      render: (_v, p) => <span className="text-sm text-muted-foreground">{p.role ?? '—'}</span>,
    },
    {
      key: 'decisionRole',
      title: t('Decision role'),
      sortable: true,
      width: '18%',
      render: (_v, p) => p.decisionRole
        ? <Badge variant="secondary" className="text-[10px]">{t(p.decisionRole)}</Badge>
        : <span className="text-xs text-muted-foreground">—</span>,
    },
    {
      key: 'influenceLevel',
      title: t('Influence'),
      sortable: true,
      width: '12%',
      render: (_v, p) => p.influenceLevel
        ? <Badge variant="secondary" className="text-[10px]">{t(p.influenceLevel)}</Badge>
        : <span className="text-xs text-muted-foreground">—</span>,
    },
    {
      key: '_action',
      title: '',
      align: 'right',
      render: (_v, p) => (
        <Button size="sm" variant="outline" onClick={() => void submitPick(p.id)} disabled={busy}>
          {t('Link')}
        </Button>
      ),
    },
  ];

  const filters: TableFilter[] = [
    {
      key: 'decisionRole',
      label: t('Decision role'),
      options: (['decider', 'influencer', 'user', 'blocker', 'champion'] as const).map((v) => ({
        value: v,
        label: t(v),
      })),
    },
    {
      key: 'influenceLevel',
      label: t('Influence level'),
      options: (['low', 'medium', 'high'] as const).map((v) => ({
        value: v,
        label: t(v),
      })),
    },
  ];

  return (
    <Modal
      isOpen={isOpen}
      onClose={resetAndClose}
      title={t('Add Persona')}
      size="2xl"
    >
      <Tabs value={mode} onValueChange={(v) => setMode(v as 'pick' | 'create')}>
        <div className="border-b border-border pb-2">
          <TabsList>
            <TabsTrigger value="pick">{t('Pick from catalog')}</TabsTrigger>
            <TabsTrigger value="create">{t('Create new persona')}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="pick">
          <Table<RorosPersonaDTO>
            data={availablePersonas}
            columns={columns}
            filters={filters}
            searchable
            searchPlaceholder={t('Search personas...')}
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: [5, 10, 25] }}
            emptyText={availablePersonas.length === 0
              ? t('All personas are already linked. Create a new one instead.')
              : t('No personas match your search.')}
          />
        </TabsContent>
        <TabsContent value="create">
          <PersonaFormBody
            onCancel={resetAndClose}
            onSubmit={async (draft) => {
              setBusy(true);
              try {
                await onCreate(draft);
                resetAndClose();
              } finally {
                setBusy(false);
              }
            }}
            submitLabel={t('Create & Link')}
            isSubmitting={busy}
          />
        </TabsContent>
      </Tabs>
    </Modal>
  );
}
